import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../components/Breadcrumbs";
import AvailableTimeSlotsCard from "../components/events/AvailableTimeSlotsCard";
import RecentlyCompletedSessionCard from "../components/recently-completed-session-card/RecentlyCompletedSessionCard";
import UpcomingSessionsCard from "../components/upcoming-session-card/UpcomingSessionsCard";
import BillingAttentionCard from "../components/billing/BillingAttentionCard";
import HomeOverviewStrip from "../components/home/HomeOverviewStrip";
import SeatRequestsCard from "../components/home/SeatRequestsCard";
import { selectSeatRequestsTotal } from "../features/sessions/seats-slice";
import OnboardingChecklist from "../components/onboarding/OnboardingChecklist";
import LeftMenu from "../components/LeftMenu";
import { useAppSelector } from "../app/hooks";
import { selectUserProfile } from "../features/authentication/authenticationSlice";
import { selectHasUpcomingSessions, selectUpcomingSessionsInitializationStatus } from "../features/sessions/selectors/upcomingSessionsSelectors";
import { selectHasRecentlyCompletedSessions, selectRecentlyCompletedSessionsInitializationStatus } from "../features/sessions/selectors/recentlyCompletedSessionsSelectors";
import { RootState } from "../app/store";
import { filterUpcomingTimeSlots } from "../features/utils/timeSlotUtils";

const HomePage = () => {
  const { t } = useTranslation();
  const userProfile = useAppSelector(selectUserProfile);
  const firstName = userProfile?.name?.split(" ")[0] || "";

  const hasUpcoming = useAppSelector(selectHasUpcomingSessions);
  const hasCompleted = useAppSelector(selectHasRecentlyCompletedSessions);
  const hasSeatRequests = useAppSelector(selectSeatRequestsTotal) > 0;
  const availableTimeSlots = useAppSelector(
    (state: RootState) => state.sessions.timeSlots.availableTimeSlots
  );
  const hasTimeSlots = useMemo(
    () => filterUpcomingTimeSlots(availableTimeSlots).length > 0,
    [availableTimeSlots]
  );

  // Wait for all data sources to finish loading before reordering
  const upcomingInit = useAppSelector(selectUpcomingSessionsInitializationStatus);
  const completedInit = useAppSelector(selectRecentlyCompletedSessionsInitializationStatus);
  const timeSlotsInit = useAppSelector(
    (state: RootState) => state.sessions.timeSlots.initializationStatus
  );
  const allInitialized =
    upcomingInit === "initialized" &&
    completedInit === "initialized" &&
    timeSlotsInit === "initialized";

  // Cards in default order, each with a hasData flag
  const cards = useMemo(() => {
    const items = [
      { key: "seatRequests", hasData: hasSeatRequests, component: <SeatRequestsCard /> },
      { key: "upcoming", hasData: hasUpcoming, component: <UpcomingSessionsCard /> },
      { key: "completed", hasData: hasCompleted, component: <RecentlyCompletedSessionCard /> },
      { key: "timeSlots", hasData: hasTimeSlots, component: <AvailableTimeSlotsCard /> },
    ];
    // Only reorder once all data has loaded to avoid layout shift
    if (!allInitialized) return items;
    return [...items].sort((a, b) => Number(b.hasData) - Number(a.hasData));
  }, [hasSeatRequests, hasUpcoming, hasCompleted, hasTimeSlots, allInitialized]);

  // Distribute cards round-robin into two columns so they pack by column
  // (each card flows under the previous one in its column) instead of locking to
  // the tallest card in a row. Only takes visual effect at the 2xl two-column layout.
  const columns = useMemo<(typeof cards)[]>(() => {
    const cols: (typeof cards)[] = [[], []];
    cards.forEach((card, index) => cols[index % 2].push(card));
    return cols;
  }, [cards]);

  return (
    <div className="flex h-full gap-4">
      {/* Left Side */}
      <LeftMenu />
      <div className="w-full p-4 pb-10 lg:px-10">
        <div className="lg:max-w-[1280px] 2xl:max-w-[1760px]">
          <Breadcrumbs />
          <div className="space-y-4 py-6 lg:space-y-6">
            <h1>
              {firstName
                ? t("home.welcomeBack", { firstName })
                : t("home.welcomeBackNoName", "Welcome Back")}
            </h1>
            {/* Today at a glance + Inbox chip + quick actions. */}
            <HomeOverviewStrip />
            {/* First-run guided path — renders only while the account is being set up. */}
            <OnboardingChecklist />
            {/* Billing items needing action sit above the schedule cards so the
                instructor can clear them with one click-through. */}
            <BillingAttentionCard />
            {/* One column normally; two column-packed columns on extra-wide (>=1536px) screens. */}
            <div className="grid grid-cols-1 items-start gap-4 lg:gap-6 2xl:grid-cols-2">
              {columns.map((col, colIndex) => (
                <div key={colIndex} className="space-y-4 lg:space-y-6">
                  {col.map((card) => (
                    <div key={card.key} className="empty:hidden">{card.component}</div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
